/**
 * CompanyEnterpriseOrders.jsx — Vague 5 (portail RH entreprise)
 *
 * Espace authentifié (company_admin) permettant de :
 *  - Lister les commandes entreprise (flow devis B2B) accessibles
 *  - Consulter le reporting détaillé par commande
 *  - Télécharger exports Excel/PDF (vue client, sans métriques internes)
 *  - Contester une consommation dans sa fenêtre 48h
 *  - Voir les factures émises
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Building2, Download, FileText, Activity, Clock, CheckCircle2,
  AlertTriangle, Loader2, Euro, ArrowLeft, Receipt, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();

const CONS_STATUS_META = {
  pending_validation: { label: 'En attente', color: '#92400E', bg: '#FEF3C7' },
  validated: { label: 'Validée', color: '#047857', bg: '#D1FAE5' },
  disputed: { label: 'Contestée', color: '#991B1B', bg: '#FEE2E2' },
  cancelled: { label: 'Annulée', color: '#6B7280', bg: '#F3F4F6' },
};

const INV_STATUS_META = {
  issued: { label: 'Émise', color: '#1E40AF', bg: '#DBEAFE' },
  paid: { label: 'Payée', color: '#047857', bg: '#D1FAE5' },
  overdue: { label: 'En retard', color: '#991B1B', bg: '#FEE2E2' },
  void: { label: 'Annulée', color: '#6B7280', bg: '#F3F4F6' },
};

export function CompanyEnterpriseOrdersList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API}/enterprise/company/my-orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Impossible de charger vos commandes');
        const data = await res.json();
        if (!cancel) setOrders(data.items || []);
      } catch (e) { toast.error(e.message); } finally { !cancel && setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [token]);

  if (loading) return <div className="p-10 text-[#6B7280]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>;

  return (
    <div className="theme-light min-h-screen bg-[#FBF9F3] pb-20">
      <div className="border-b border-[#E8E5DE] bg-white">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <button onClick={() => navigate(-1)}
            data-testid="company-back"
            className="inline-flex items-center gap-1.5 text-[#6B7280] hover:text-[#111827] text-sm mb-4">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">Entreprise</p>
          <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] mt-1">
            Mes commandes de coaching
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Suivi de consommation, contestation, exports et facturation pour vos programmes B2B.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {orders.length === 0 ? (
          <div className="text-sm text-[#6B7280] py-10 text-center bg-white border border-[#E8E5DE] rounded-2xl">
            Aucune commande entreprise active.
            <br />
            <Link to="/subscription/enterprise" className="text-[#92710A] underline text-xs mt-2 inline-block">
              Démarrer une demande de devis
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="company-orders-list">
            {orders.map(o => (
              <Link to={`/entreprise/commandes/${o.id}`} key={o.id}
                data-testid={`company-order-${o.id}`}
                className="block bg-white border border-[#E8E5DE] rounded-2xl p-4 hover:border-[#92710A] transition">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#92710A]" />
                      <span className="text-sm font-display font-medium text-[#111827]">
                        {o.company_snapshot?.company_name || '—'}
                      </span>
                      <span className="text-[10px] uppercase font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">
                        {o.type === 'ia' ? 'Coaching IA' : 'Coaching Humain'}
                      </span>
                    </div>
                    <div className="text-xs text-[#6B7280] mt-1 font-mono">Devis {o.quote_number}</div>
                  </div>
                  {o.basket_summary && (
                    <div className="text-right">
                      <div className="text-xs text-[#4B5563]">
                        <b className="text-[#111827]">{o.basket_summary.consumed_sessions}</b> / {o.basket_summary.total_sessions} sessions
                      </div>
                      <div className="w-32 h-1.5 rounded-full bg-[#F3F4F6] mt-1 overflow-hidden">
                        <div className="h-full bg-[#92710A]"
                          style={{ width: `${(o.basket_summary.consumed_sessions / o.basket_summary.total_sessions) * 100}%` }} />
                      </div>
                    </div>
                  )}
                  <ChevronRight className="w-4 h-4 text-[#92710A]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CompanyEnterpriseOrderReport() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [consumptions, setConsumptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRep, rInv, rCons] = await Promise.all([
        fetch(`${API}/enterprise/company/orders/${id}/report`, { headers }),
        fetch(`${API}/enterprise/company/orders/${id}/invoices`, { headers }),
        fetch(`${API}/enterprise/company/orders/${id}/consumptions`, { headers }),
      ]);
      if (!rRep.ok) throw new Error('Accès refusé ou commande introuvable');
      const rep = await rRep.json();
      setReport(rep);
      if (rInv.ok) setInvoices((await rInv.json()).items || []);
      if (rCons.ok) setConsumptions((await rCons.json()).items || []);
    } catch (e) { toast.error(e.message); navigate('/entreprise/commandes'); } finally { setLoading(false); }
  }, [id, token, navigate]); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const download = async (type) => {
    const res = await fetch(`${API}/enterprise/company/orders/${id}/report/export/${type}`, { headers });
    if (!res.ok) { toast.error('Export impossible'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rapport_${report.order.quote_number}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
    a.click();
  };

  const dispute = async (cid) => {
    const reason = window.prompt(
      'Motif de contestation (au moins 3 caractères). Vous disposez de 48h après la session pour contester.',
    );
    if (!reason || reason.length < 3) return;
    const res = await fetch(`${API}/enterprise/company/consumption/${cid}/dispute`, {
      method: 'POST', headers, body: JSON.stringify({ reason }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Erreur'); return; }
    toast.success('Contestation enregistrée — un administrateur arbitrera sous peu.');
    fetchAll();
  };

  if (loading || !report) {
    return <div className="p-10 text-[#6B7280]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>;
  }
  const { order, synthesis: syn, collaborators } = report;
  const snap = order.company_snapshot || {};

  return (
    <div className="theme-light min-h-screen bg-[#FBF9F3] pb-20">
      <div className="border-b border-[#E8E5DE] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <button onClick={() => navigate('/entreprise/commandes')}
            data-testid="company-report-back"
            className="inline-flex items-center gap-1.5 text-[#6B7280] hover:text-[#111827] text-sm mb-4">
            <ArrowLeft className="w-4 h-4" /> Mes commandes
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">
                Suivi — {order.type === 'ia' ? 'Coaching IA' : 'Coaching Humain'}
              </p>
              <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] mt-1">{snap.company_name}</h1>
              <p className="text-sm text-[#6B7280] mt-1 font-mono">Devis {order.quote_number}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => download('excel')}
                data-testid="company-export-excel"
                className="px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] text-[#4B5563] text-xs font-medium hover:bg-[#EBEAE3] inline-flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Excel
              </button>
              <button onClick={() => download('pdf')}
                data-testid="company-export-pdf"
                className="px-3 py-1.5 rounded-full bg-[#111827] text-white text-xs font-medium hover:bg-[#1f2937] inline-flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        {/* KPIs client */}
        <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E8E5DE] rounded-2xl p-5"
          data-testid="company-report-kpis">
          <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#92710A]" /> Vue consolidée
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KPI label="Sessions achetées" value={syn.sessions_purchased} />
            <KPI label="Consommées" value={syn.sessions_consumed} accent="#047857" />
            <KPI label="Allouées" value={syn.sessions_allocated} accent="#1E40AF" />
            <KPI label="Restantes" value={syn.sessions_remaining} accent="#92710A" />
            <KPI label="Validées" value={syn.consumption_validated} accent="#047857" icon={CheckCircle2} />
            <KPI label="En attente" value={syn.consumption_pending} accent="#92400E" icon={Clock} />
            <KPI label="Contestées" value={syn.consumption_disputed} accent="#991B1B" icon={AlertTriangle} />
            <KPI label="Budget total HT" value={`${(syn.budget_total_ht || 0).toLocaleString('fr-FR')} €`} icon={Euro} />
          </div>
          <div className="mt-3">
            <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">Progression globale</div>
            <div className="relative h-3 rounded-full bg-[#F3F4F6] overflow-hidden">
              <div className="absolute top-0 left-0 h-full bg-[#92710A]"
                style={{ width: `${syn.progression_pct || 0}%` }} />
            </div>
            <div className="text-[10px] text-[#6B7280] mt-1">{syn.progression_pct || 0}% consommé</div>
          </div>
        </motion.section>

        {/* Collaborateurs */}
        <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5"
          data-testid="company-collaborators-section">
          <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#92710A]" /> Collaborateurs ({collaborators?.length || 0})
          </h2>
          <div className="overflow-x-auto border border-[#E8DFC8] rounded-xl bg-white">
            <table className="w-full text-[12px]">
              <thead className="bg-[#F8F7F4] text-[#6B7280]">
                <tr>
                  <th className="p-2 text-left">Collaborateur</th>
                  <th className="p-2 text-right">Quota</th>
                  <th className="p-2 text-right">Consommé</th>
                  <th className="p-2 text-right">Validées</th>
                  <th className="p-2 text-right">En attente</th>
                  <th className="p-2 text-right">Contestées</th>
                </tr>
              </thead>
              <tbody>
                {collaborators?.map(c => (
                  <tr key={c.collaborator_id} className="border-t border-[#F0EEE8] hover:bg-[#FBF9F3]">
                    <td className="p-2"><b>{c.name}</b><div className="text-[10px] text-[#6B7280]">{c.email}</div></td>
                    <td className="p-2 text-right">{c.sessions_allocated}</td>
                    <td className="p-2 text-right font-medium">{c.sessions_used}</td>
                    <td className="p-2 text-right text-[#047857]">{c.sessions_validated}</td>
                    <td className="p-2 text-right text-[#92400E]">{c.sessions_pending}</td>
                    <td className="p-2 text-right text-[#991B1B]">{c.sessions_disputed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-[#6B7280] mt-2">
            Pour contester une session spécifique, contactez votre admin CoachDIA dans la fenêtre de 48h.
            Un mode d'auto-contestation détaillé est disponible sous chaque session consommée via notre interface premium.
          </p>
        </section>

        {/* Sessions consommées — contestation granulaire 48h */}
        <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5"
          data-testid="company-consumptions-section">
          <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#92710A]" /> Sessions consommées ({consumptions.length})
          </h2>
          {consumptions.length === 0 ? (
            <div className="text-sm text-[#6B7280] py-5 text-center bg-[#FBF9F3] border border-[#E8DFC8] rounded-xl">
              Aucune session n'a encore été réalisée.
            </div>
          ) : (
            <div className="overflow-x-auto border border-[#E8DFC8] rounded-xl bg-white">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F8F7F4] text-[#6B7280]">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Collaborateur</th>
                    <th className="p-2 text-center">Statut</th>
                    <th className="p-2 text-left">Deadline contestation</th>
                    <th className="p-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {consumptions.slice(0, 100).map(c => {
                    const m = CONS_STATUS_META[c.status] || {};
                    const now = new Date();
                    const dl = c.validation_deadline ? new Date(c.validation_deadline) : null;
                    const canDispute = c.status === 'pending_validation' && dl && dl > now;
                    return (
                      <tr key={c.id} className="border-t border-[#F0EEE8] hover:bg-[#FBF9F3]">
                        <td className="p-2 text-[#6B7280]">{(c.session_date || c.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="p-2">{c.collaborator_email}</td>
                        <td className="p-2 text-center">
                          <span className="inline-flex text-[10px] uppercase font-medium px-2 py-0.5 rounded-full"
                            style={{ color: m.color, backgroundColor: m.bg }}>{m.label}</span>
                        </td>
                        <td className="p-2 text-[#6B7280]">
                          {dl ? dl.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </td>
                        <td className="p-2 text-right">
                          {canDispute && (
                            <button data-testid={`company-dispute-${c.id}`}
                              onClick={() => dispute(c.id)}
                              className="px-2 py-0.5 rounded-full bg-[#DC2626] text-white text-[10px] hover:bg-[#991B1B]">
                              Contester
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Factures */}
        <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5"
          data-testid="company-invoices-section">
          <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-[#92710A]" /> Factures ({invoices.length})
          </h2>
          {invoices.length === 0 ? (
            <div className="text-sm text-[#6B7280] py-5 text-center bg-[#FBF9F3] border border-[#E8DFC8] rounded-xl">
              Aucune facture émise à ce jour.
            </div>
          ) : (
            <div className="overflow-x-auto border border-[#E8DFC8] rounded-xl bg-white">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F8F7F4] text-[#6B7280]">
                  <tr>
                    <th className="p-2 text-left">N°</th>
                    <th className="p-2 text-left">Mode</th>
                    <th className="p-2 text-left">Période</th>
                    <th className="p-2 text-right">Total TTC</th>
                    <th className="p-2 text-center">Statut</th>
                    <th className="p-2 text-left">Échéance</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const m = INV_STATUS_META[inv.status] || { label: inv.status, color: '#6B7280', bg: '#F3F4F6' };
                    return (
                      <tr key={inv.id} className="border-t border-[#F0EEE8]">
                        <td className="p-2 font-mono font-medium">{inv.invoice_number}</td>
                        <td className="p-2 text-[#6B7280] capitalize">{inv.mode}</td>
                        <td className="p-2 text-[#6B7280]">
                          {inv.period_start ? `${inv.period_start.slice(0, 10)} → ${inv.period_end.slice(0, 10)}` : '—'}
                        </td>
                        <td className="p-2 text-right font-medium">{inv.total_ttc.toLocaleString('fr-FR')} €</td>
                        <td className="p-2 text-center">
                          <span className="inline-flex text-[10px] uppercase font-medium px-2 py-0.5 rounded-full"
                            style={{ color: m.color, backgroundColor: m.bg }}>{m.label}</span>
                        </td>
                        <td className="p-2 text-[#6B7280]">{(inv.due_date || '').slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function KPI({ label, value, accent = '#111827', icon: Icon }) {
  return (
    <div className="p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />}
      </div>
      <div className="text-lg font-display mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
